import { useAuth0 } from '@auth0/auth0-react';
import {
  useAddress,
  useCreateAddress,
  useCreateSite,
  useDeleteOrganisationLogo,
  useOrganisation,
  useUpdateAddress,
  useUpdateOrganisation,
  useUploadOrganisationLogo,
} from '@my/directory-api/hooks';
import {
  AddressInlineEditor,
  Button,
  ErrorBanner,
  FormSection,
  Loading,
  LogoUploader,
  PageHeader,
  RegionalSettingsInlineEditor,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import {
  applyCountryDefaults,
  COUNTRIES,
  DATE_PATTERNS,
  LANGUAGES,
  TIME_PATTERNS,
  TIMEZONES,
} from '@my/platform/options';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { AddNewAddressPanel } from './AddNewAddressPanel';
import {
  EMPTY_ADDRESS_FORM,
  toAddressForm,
  toCreateAddressRequest,
  toCreateSiteRequest,
  toOrgForm,
  toUpdateAddressRequest,
  toUpdateOrganisationRequest,
  type AddressForm,
  type OrganisationForm,
} from './lib/formMappers';

// ────────────────────────────────────────────────────────────────────────────
// Dropdown options. Source-of-truth lists live in `@my/platform/options`;
// here we just shape them into the `SelectInputOption` form the UI primitives
// expect. Country labels carry the flag emoji as a small visual cue.
// ────────────────────────────────────────────────────────────────────────────

const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));

const TIMEZONE_OPTIONS: SelectInputOption[] = TIMEZONES.map((z) => ({
  value: z.iana,
  label: z.label,
}));

const LANGUAGE_OPTIONS: SelectInputOption[] = LANGUAGES.map((l) => ({
  value: l.isoCode,
  label: `${l.name} (${l.nativeName})`,
}));

const TIME_PATTERN_OPTIONS: SelectInputOption[] = TIME_PATTERNS.map((t) => ({
  value: t.pattern,
  label: t.label,
}));

const DATE_PATTERN_OPTIONS: SelectInputOption[] = DATE_PATTERNS.map((d) => ({
  value: d.pattern,
  label: d.label,
}));

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

/**
 * Safety · Settings/Organisation. A trimmed port of the TMS Organisation page:
 * the single "Organisation Details" card with the company-identity, address and
 * regional fields. The finance/TMS-only surfaces (invoice & sequence config,
 * order reference labels, account system, VAT rounding, base currency, custom
 * fields, plus the Administrators / Currencies / Bank Accounts / Tax / Nominal
 * cards) are intentionally dropped — Safety has no invoicing or orders.
 *
 * The org PUT is still a full replace, so `formMappers` echoes the
 * non-rendered finance fields back unchanged (see its OrganisationForm note).
 */
export function OrganisationPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['organisation', 'common']);

  const orgQuery = useOrganisation(orgId);
  const updateOrgMutation = useUpdateOrganisation();
  const uploadLogoMutation = useUploadOrganisationLogo();
  const deleteLogoMutation = useDeleteOrganisationLogo();

  const addressId = orgQuery.data?.mainOfficeAddressId ?? null;
  const addressQuery = useAddress(addressId);
  const createAddressMutation = useCreateAddress();
  const updateAddressMutation = useUpdateAddress();
  const createSiteMutation = useCreateSite();

  const [orgForm, setOrgForm] = useState<OrganisationForm | null>(null);
  const [addressForm, setAddressForm] = useState<AddressForm>(EMPTY_ADDRESS_FORM);
  const [addressSubmitted, setAddressSubmitted] = useState(false);

  useEffect(() => {
    if (orgQuery.data) setOrgForm(toOrgForm(orgQuery.data));
  }, [orgQuery.data, orgQuery.dataUpdatedAt]);

  useEffect(() => {
    if (addressQuery.data) setAddressForm(toAddressForm(addressQuery.data));
    else if (!addressId) setAddressForm(EMPTY_ADDRESS_FORM);
  }, [addressQuery.data, addressQuery.dataUpdatedAt, addressId]);

  const setOrgField = <K extends keyof OrganisationForm>(key: K, value: OrganisationForm[K]) =>
    setOrgForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleAddressChange = (next: AddressForm) => {
    const previousCountry = addressForm.countryIsoCode;
    const newCountry = next.countryIsoCode;

    if (newCountry && newCountry !== previousCountry) {
      const patches = applyCountryDefaults({
        newCountry,
        previousCountry: previousCountry || null,
        current: orgForm
          ? {
              languageCode: orgForm.languageCode,
              currencyIsoCode: orgForm.currencyIsoCode,
              operationalTimeZone: orgForm.operationalTimeZone,
              datePattern: orgForm.datePattern,
              timePattern: orgForm.timePattern,
            }
          : {},
      });
      if (Object.keys(patches).length > 0) {
        setOrgForm((prev) => (prev ? { ...prev, ...patches } : prev));
      }
    }

    setAddressForm(next);
  };

  const addressHasAnyInput =
    addressForm.fullAddress.trim().length > 0 ||
    addressForm.city.trim().length > 0 ||
    addressForm.postcode.trim().length > 0;

  const addressMissingFullAddress = addressHasAnyInput && !addressForm.fullAddress.trim();
  const addressMissingPostcode = addressHasAnyInput && !addressForm.postcode.trim();
  const addressMissingCountry = addressHasAnyInput && !addressForm.countryIsoCode.trim();
  const addressMissingLocation =
    addressHasAnyInput && (addressForm.latitude == null || addressForm.longitude == null);
  const addressInvalid =
    addressMissingFullAddress ||
    addressMissingPostcode ||
    addressMissingCountry ||
    addressMissingLocation;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!orgId || !orgQuery.data || !orgForm) return;
    if (!orgForm.name.trim() || !orgForm.invoicingName.trim()) return;

    // Validate the address-create form only when there is no existing address
    // yet. The update path is validated lower down (existing-address branch).
    if (!addressId && addressInvalid) {
      setAddressSubmitted(true);
      showToast({
        type: 'error',
        message: addressMissingLocation
          ? t('organisation:validation.addressVerifyLocation')
          : t('organisation:validation.addressRequired'),
      });
      return;
    }

    try {
      let mainOfficeAddressId = orgQuery.data.mainOfficeAddressId ?? null;
      const wantsAddressCreate =
        !addressId &&
        addressForm.fullAddress.trim().length > 0 &&
        addressForm.countryIsoCode.trim().length > 0;

      if (wantsAddressCreate) {
        mainOfficeAddressId = await createAddressMutation.mutateAsync(
          toCreateAddressRequest(orgId, addressForm),
        );
        await createSiteMutation.mutateAsync(toCreateSiteRequest(orgId, mainOfficeAddressId));
      }

      await updateOrgMutation.mutateAsync(
        toUpdateOrganisationRequest(orgQuery.data, orgForm, mainOfficeAddressId),
      );

      // Update existing main-office address.
      if (addressId && addressQuery.data) {
        if (!addressForm.fullAddress.trim() || !addressForm.countryIsoCode.trim()) {
          showToast({
            type: 'error',
            message: t('organisation:validation.fullAddressAndCountryRequired'),
          });
          return;
        }
        if (!addressForm.postcode.trim()) {
          showToast({ type: 'error', message: t('organisation:validation.postcodeRequired') });
          return;
        }
        if (addressForm.latitude == null || addressForm.longitude == null) {
          showToast({
            type: 'error',
            message: t('organisation:validation.addressVerifyLocation'),
          });
          return;
        }
        await updateAddressMutation.mutateAsync(
          toUpdateAddressRequest(addressQuery.data, addressForm),
        );
      }

      showToast({ type: 'success', message: t('organisation:toasts.saved') });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('organisation:toasts.saveFailed');
      showToast({ type: 'error', message });
    }
  };

  const isSaving =
    updateOrgMutation.isPending ||
    createAddressMutation.isPending ||
    updateAddressMutation.isPending ||
    createSiteMutation.isPending;
  const isLogoUploading = uploadLogoMutation.isPending || deleteLogoMutation.isPending;

  const handleLogoUpload = (file: File) => {
    if (!orgId) return;
    uploadLogoMutation.mutate(
      { id: orgId, file },
      {
        onSuccess: () =>
          showToast({ type: 'success', message: t('organisation:toasts.logoUpdated') }),
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : t('organisation:toasts.logoUploadFailed'),
          }),
      },
    );
  };

  const handleLogoClear = () => {
    if (!orgId) return;
    deleteLogoMutation.mutate(
      { id: orgId },
      {
        onSuccess: () =>
          showToast({ type: 'success', message: t('organisation:toasts.logoRemoved') }),
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : t('organisation:toasts.logoRemoveFailed'),
          }),
      },
    );
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={t('organisation:title')} description={t('organisation:description')} />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle')}
            message={t('common:errors.noOrgLinkedMessage')}
          />
        )}

        {orgQuery.isError && (
          <ErrorBanner
            title={t('organisation:errors.loadFailed')}
            message={orgQuery.error.message}
          />
        )}

        {orgQuery.isPending && orgId && (
          <div className="rounded-md border border-slate-200 bg-white">
            <Loading fullHeight label={t('organisation:loading.org')} />
          </div>
        )}

        {orgQuery.isSuccess && orgForm && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <FormSection title={t('organisation:formSection.details')} columns={2}>
              {/* ── Column 1 ── Logo, Name, Address, Regional ─ */}
              <div className="space-y-3 text-sm">
                <LogoUploader
                  label={t('organisation:fields.logo')}
                  value={orgQuery.data?.logoUrl ?? null}
                  onUpload={handleLogoUpload}
                  onClear={handleLogoClear}
                  isUploading={isLogoUploading}
                />
                <TextInput
                  label={t('organisation:fields.name')}
                  name="name"
                  value={orgForm.name}
                  onChange={(e) => setOrgField('name', e.target.value)}
                  error={!orgForm.name.trim()}
                  errorMessage={t('organisation:validation.nameRequired')}
                />

                {addressQuery.data ? (
                  <AddressInlineEditor
                    label={t('organisation:fields.address')}
                    value={addressForm}
                    onChange={handleAddressChange}
                    countryOptions={COUNTRY_OPTIONS}
                    enablePlacesSearch
                    hasCodeField
                  />
                ) : addressId && addressQuery.isPending ? (
                  <p className="text-xs text-slate-500">{t('organisation:loading.address')}</p>
                ) : addressId && addressQuery.isError ? (
                  <p className="text-xs text-red-600">
                    {addressQuery.error?.message ?? t('organisation:errors.loadAddressFailed')}
                  </p>
                ) : (
                  <AddNewAddressPanel
                    value={addressForm}
                    onChange={handleAddressChange}
                    countryOptions={COUNTRY_OPTIONS}
                    showErrors={addressSubmitted}
                    missingFullAddress={addressMissingFullAddress}
                    missingPostcode={addressMissingPostcode}
                    missingCountry={addressMissingCountry}
                  />
                )}

                <RegionalSettingsInlineEditor
                  label={t('organisation:fields.regionalSettings')}
                  value={{
                    operationalTimeZone: orgForm.operationalTimeZone,
                    languageCode: orgForm.languageCode,
                    timePattern: orgForm.timePattern,
                    datePattern: orgForm.datePattern,
                  }}
                  onChange={(next) => setOrgForm((prev) => (prev ? { ...prev, ...next } : prev))}
                  timeZoneOptions={TIMEZONE_OPTIONS}
                  languageOptions={LANGUAGE_OPTIONS}
                  timePatternOptions={TIME_PATTERN_OPTIONS}
                  datePatternOptions={DATE_PATTERN_OPTIONS}
                />
              </div>

              {/* ── Column 2 ── Invoicing Name, Network ID, Tax / Reg Number ── */}
              <div className="space-y-3 text-sm">
                <TextInput
                  label={t('organisation:fields.invoicingName')}
                  name="invoicingName"
                  value={orgForm.invoicingName}
                  onChange={(e) => setOrgField('invoicingName', e.target.value)}
                  error={!orgForm.invoicingName.trim()}
                  errorMessage={t('organisation:validation.invoicingNameRequired')}
                />

                <TextInput
                  label={'Network ID'}
                  name="networkId"
                  value={orgForm.networkId}
                  readOnly
                  disabled
                  onChange={(e) => setOrgField('networkId', e.target.value)}
                />

                <TextInput
                  label={t('organisation:fields.taxNumber')}
                  name="taxNumber"
                  value={orgForm.taxNumber}
                  onChange={(e) => setOrgField('taxNumber', e.target.value)}
                />

                <TextInput
                  label={t('organisation:fields.registrationNumber')}
                  name="registrationNumber"
                  value={orgForm.registrationNumber}
                  onChange={(e) => setOrgField('registrationNumber', e.target.value)}
                />
              </div>

              {/* ── Footer ── tiny green tick at the bottom-right, matches TMS ── */}
              <div className="flex justify-end p-0 md:col-span-2">
                <Button
                  type="submit"
                  variant="custom"
                  size="xs"
                  icon={Check}
                  ariaLabel={t('organisation:aria.save')}
                  loading={isSaving}
                  className="p-1 text-green-600 hover:bg-green-50 [&_svg]:h-3.5 [&_svg]:w-3.5"
                />
              </div>
            </FormSection>
          </form>
        )}
      </main>
    </div>
  );
}
